import { useCallback, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { selectExpandedRows, selectPrimarySecurity } from "../selectors/home"
import { getSecurityKey, isFnOSecurity } from "../util/security"
import { selectUnderlyingOptionsTradeData } from "../selectors/options"
import { selectCommonStore } from "../selectors/common"
import { setOptionChainExpandedRows } from "../reducer/home.reducer"
import { addStrategyOrder, calculateDifferenceInDays, findNearestStrikeData, getOrderForStrategy, StrategiesValue } from "../util/strategyBuilder"
import { setPredefinedStrategy } from "../reducer/strategyBuilder.reducer"
import { fetchOptionChainV2, fetchOptionChainV2Initial } from "../util/options"
import { matchPath, useLocation } from "react-router-dom"
import ROUTES from "../assets/routes"
import { OptionChainViewKey } from "../assets/constants"
import { GetHistoricOptionChainRequest } from "../services/apis/simulator"
import { selectSimulatorDate } from "../selectors/simulator"
import { optionsTradesDataType } from "../pages/typings/options.typings"

const useOnStrategySelected = () => {
    const location = useLocation()
    const { expressionMode, primary, underlying, key } = useSelector(selectPrimarySecurity)
    const mainSecurity = expressionMode
        ? underlying
        : primary && isFnOSecurity(primary)
            ? underlying
            : primary
    const optionChainViewKey = useMemo(
        () => (matchPath(ROUTES.simulator, location.pathname) ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE),
        [location.pathname]
    )
    const underlyingToken = mainSecurity ? getSecurityKey(mainSecurity.exchange, mainSecurity.code) : ''
    const optionsExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const { rightSideWidget } = useSelector(selectCommonStore)
    const dispatch = useDispatch()
    const [loading, setLoading] = useState<boolean>(false)

    const updateExpandedRows = useCallback(
        (rows: string[]) => {
            dispatch(setOptionChainExpandedRows({
                expandedRows: rows,
                optionChainViewKey: optionChainViewKey
            }))
        },
        [dispatch, optionChainViewKey]
    )

    const ensureOptionExpiries = useCallback(async (current: optionsTradesDataType[] | undefined, expiryIndex: number) => {
        let newOptionExpiries = current ? [...current] : []
        const needsExpiryTrades = !newOptionExpiries[expiryIndex] || newOptionExpiries[expiryIndex].trades.length === 0

        if (optionChainViewKey === OptionChainViewKey.HISTORICAL) {
            if (!mainSecurity || !simulatorTimestamp) return newOptionExpiries
            if (!newOptionExpiries.length || needsExpiryTrades) {
                const payload: GetHistoricOptionChainRequest = {
                    security: {
                        code: mainSecurity.code,
                        exchange: mainSecurity.exchange
                    },
                    referenceTimestamp: simulatorTimestamp
                }
                const rows = expandedRows.length > 0 ? expandedRows : ['0']
                const { optionsData } = await fetchOptionChainV2(
                    payload,
                    dispatch,
                    newOptionExpiries.length ? newOptionExpiries : undefined,
                    'both',
                    true,
                    rows,
                    simulatorTimestamp
                )
                newOptionExpiries = optionsData
            }
            return newOptionExpiries
        }

        if (!primary) return newOptionExpiries
        if (!newOptionExpiries.length) {
            const { optionsData } = await fetchOptionChainV2Initial(primary, dispatch)
            newOptionExpiries = optionsData
        }
        if (newOptionExpiries[expiryIndex] && newOptionExpiries[expiryIndex].trades.length === 0) {
            const { optionsData } = await fetchOptionChainV2Initial(
                primary,
                dispatch,
                true,
                [Number(newOptionExpiries[expiryIndex].expirySeconds)],
                newOptionExpiries
            )
            newOptionExpiries = optionsData
        }
        return newOptionExpiries
    }, [optionChainViewKey, mainSecurity, simulatorTimestamp, expandedRows, dispatch, primary])

    const onStrategySelected = useCallback(
        async (strategyLabel: StrategiesValue) => {
            if (!key || !mainSecurity) return
            setLoading(true)
            try {
                const index = Number(expandedRows.length > 0 ? expandedRows[0] : '0')
                if (expandedRows.length === 0) updateExpandedRows(['0'])

                let newOptionExpiries = await ensureOptionExpiries(optionsExpiries, index)
                if (!newOptionExpiries[index]) return

                if (
                    (strategyLabel === StrategiesValue.Long_Calendar_With_Calls || strategyLabel === StrategiesValue.Long_Calendar_With_Puts)
                ) {
                    const monthExpiries = newOptionExpiries.filter((option) => !option.expiration.weeklyExpiry && !option.derived)
                    let firstIndex = index
                    let secondIndex: number = -1
                    monthExpiries.some((expiry) => {
                        if (calculateDifferenceInDays(expiry.expirySeconds ?? '0', newOptionExpiries[firstIndex].expirySeconds ?? '0') >= 14) {
                            secondIndex = Number(expiry.key)
                            return true
                        }
                        return false
                    })
                    if (!secondIndex || secondIndex === -1) {
                        firstIndex = 0
                        monthExpiries.some((expiry) => {
                            if (calculateDifferenceInDays(expiry.expirySeconds ?? '0', newOptionExpiries[firstIndex].expirySeconds ?? '0') >= 14) {
                                secondIndex = Number(expiry.key)
                                return true
                            }
                            return false
                        })
                    }
                    if (secondIndex >= 0 && newOptionExpiries[secondIndex]?.trades.length === 0) {
                        newOptionExpiries = await ensureOptionExpiries(newOptionExpiries, secondIndex)
                    }
                }

                const expiryData = newOptionExpiries[index]
                let atmStrikeData = expiryData?.trades.find((t) => t.strikeCol.strike === expiryData.atmStrike)
                if (!atmStrikeData && expiryData?.trades?.length && expiryData.atmStrike) {
                    atmStrikeData = findNearestStrikeData(expiryData.atmStrike, expiryData)
                }
                if (!atmStrikeData) return

                const sdPercent = expiryData.standardDeviation
                const oneSdWidth = atmStrikeData.strikeCol.strike * (sdPercent / 100)
                const orderStrategy = getOrderForStrategy(strategyLabel, atmStrikeData, oneSdWidth, newOptionExpiries, index, updateExpandedRows)
                if (!orderStrategy?.length) return

                dispatch(setPredefinedStrategy(strategyLabel))
                await addStrategyOrder(orderStrategy, rightSideWidget, dispatch, newOptionExpiries, underlyingToken, orderStrategy.length > 1)
            } finally {
                setLoading(false)
            }
        },
        [
            key,
            mainSecurity,
            expandedRows,
            updateExpandedRows,
            ensureOptionExpiries,
            optionsExpiries,
            dispatch,
            rightSideWidget,
            underlyingToken
        ]
    )

    return {
        onStrategySelected,
        loading
    }
}

export default useOnStrategySelected
